import { useEffect, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { gridQuery, type Market } from "./api";
import { ANCHOR_OPTIONS, anchorValue, cleanEmail, cleanNumber, cleanPositive, cleanWhole, CONFIG_COPY as T, CONFIG_KEY, configQuery, deleteFloor, deleteSetting, deleteRrp, deleteServing, ENTITY_LABEL, floorSourceLabel, fxSourceLabel, MARKETS, numberValue, putFloor, putRrp, putServing, putSetting, STALE_HOURS, unmappedInputs, unmappedOf, unmappedTotal, writeRefusal, type ConfigRead, type FloorRow, type RrpProduct, type ServingProduct, type ServingSizes, type Setting } from "./config";
import { CUSTOMER_KEY } from "./customer";
import { MARKET_LABEL, messageFor, money, stamp } from "./format";
import { MARGIN_WEEKS_KEY } from "./margin_weeks";
import { PROJECTION_KEY } from "./projection";
import { EVAL_KEY } from "./strategies";

type Result = { tone: "ok" | "err"; text: string } | null;
const setBy = (s: { set_by: string | null; set_at: string | null }) => T.setBy(s.set_by, s.set_at, (iso) => stamp(iso));

/**
 * The Config tab (Canvas v25 renderConfig): one read, seven sections (C1 v1.20 adds Serving sizes). Every value reads "not set" with C2's sentence until
 * Damien sets it; a save shows the store's own sentence when it refuses. A view-only person sees the same controls; the
 * shell renders them inert and C2 refuses the write, which the result line then says (as the Price Run's New price box).
 */
export function ConfigTab({ areaName }: { areaName: string }) {
  const read = useQuery(configQuery());
  const heading = <div className="page-head"><h2 className="text-lg font-semibold text-ks-ink">{T.title}</h2><p className="text-sm text-ks-grey-1">{T.desc}</p></div>;
  if (read.isPending) return <div className="flex flex-col gap-3">{heading}<Card><CardContent className="flex flex-col gap-2 py-4">{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-7 w-full" />)}</CardContent></Card></div>;
  if (read.error) return <div className="flex flex-col gap-3">{heading}<Alert role="alert" variant="destructive"><AlertDescription>{messageFor(read.error, areaName)}</AlertDescription></Alert></div>;
  const d = read.data;
  return (
    <div className="flex flex-col gap-6">
      {heading}
      <NumberSetting setting={d.settings.margin_target_pct} copy={T.target} areaName={areaName} />
      <NumberSetting setting={d.settings.weekly_move_threshold_pts} copy={T.move} areaName={areaName} />
      {d.settings.weekly_move_owner && <OwnerSetting setting={d.settings.weekly_move_owner} areaName={areaName} />}
      <RrpSection data={d} areaName={areaName} />
      <FloorsSection floors={d.floors} areaName={areaName} />
      <ServingSection sizes={d.serving_sizes} areaName={areaName} />
      <LinksSection data={d} />
      <StaleSetting setting={d.settings.stale_basis_hours} areaName={areaName} />
    </div>
  );
}

function ResultLine({ result }: { result: Result }) {
  if (!result) return null;
  return <p role="status" className={`text-sm ${result.tone === "ok" ? "text-ks-pass" : "text-ks-danger"}`}>{result.text}</p>;
}

function Section({ title, desc, children }: { title: string; desc: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <div><h3 className="text-base font-semibold text-ks-ink">{title}</h3><p className="text-sm text-ks-grey-1">{desc}</p></div>
      <Card><CardContent className="flex flex-col gap-3 py-4">{children}</CardContent></Card>
    </section>
  );
}

/** A company number (the margin target, the weekly-move threshold): one input, Save, who set it, or C2's "not set" sentence. */
function NumberSetting({ setting, copy, areaName }: { setting: Setting; copy: { title: string; label: string; unit: string; desc: string }; areaName: string }) {
  const client = useQueryClient();
  const current = numberValue(setting) ?? "";
  const [value, setValue] = useState(current);
  const [result, setResult] = useState<Result>(null);
  // The read is the truth: when a save on another tab changes it, the box follows (as the New price box does).
  useEffect(() => { setValue(current); }, [current]);
  const save = useMutation({
    mutationFn: (v: string) => putSetting(setting.key, { value: v }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: CONFIG_KEY }); setResult({ tone: "ok", text: T.saved }); },
    onError: (error) => setResult({ tone: "err", text: writeRefusal(error, areaName) }),
  });
  const submit = () => {
    const v = cleanNumber(value);
    if (!v) { setResult({ tone: "err", text: T.enterNumber }); return; }
    save.mutate(v);
  };
  const id = `cfg-${setting.key}`;
  return (
    <Section title={copy.title} desc={copy.desc}>
      <div className="flex flex-wrap items-center gap-3">
        <Label htmlFor={id} className="font-semibold">{copy.label}</Label>
        <Input id={id} inputMode="decimal" className="w-24" value={value} onChange={(e) => setValue(e.target.value)} />
        <span className="text-sm text-ks-grey-1">{copy.unit}</span>
        <Button size="sm" onClick={submit} disabled={save.isPending || value.trim() === current}>{save.isPending ? T.saving : T.save}</Button>
        {setting.not_set ? <Badge variant="outline">{setting.not_set}</Badge> : <span className="text-xs text-ks-grey-1">{setBy(setting)}</span>}
      </div>
      <ResultLine result={result} />
    </Section>
  );
}

/** The weekly-move owner (C1 v1.62, C2 v1.59): one email, Save and Remove, who set it, or C2's "not set" sentence. Overview's
 * Weekly moves names the owner, so a save or a removal drops its cache with Config's. */
function OwnerSetting({ setting, areaName }: { setting: Setting; areaName: string }) {
  const client = useQueryClient();
  const current = typeof setting.value === "string" ? setting.value : "";
  const [value, setValue] = useState(current);
  const [result, setResult] = useState<Result>(null);
  useEffect(() => { setValue(current); }, [current]);
  const refresh = () => { for (const queryKey of [CONFIG_KEY, MARGIN_WEEKS_KEY]) void client.invalidateQueries({ queryKey }); };
  const save = useMutation({
    mutationFn: (v: string) => putSetting("weekly_move_owner", { value: v }),
    onSuccess: () => { refresh(); setResult({ tone: "ok", text: T.saved }); },
    onError: (error) => setResult({ tone: "err", text: writeRefusal(error, areaName) }),
  });
  const remove = useMutation({
    mutationFn: () => deleteSetting("weekly_move_owner"),
    onSuccess: () => { refresh(); setValue(""); setResult({ tone: "ok", text: T.removed }); },
    onError: (error) => setResult({ tone: "err", text: writeRefusal(error, areaName) }),
  });
  const submit = () => {
    const v = cleanEmail(value);
    if (!v) { setResult({ tone: "err", text: T.owner.enterEmail }); return; }
    save.mutate(v);
  };
  const id = "cfg-weekly_move_owner";
  return (
    <Section title={T.owner.title} desc={T.owner.desc}>
      <div className="flex flex-wrap items-center gap-3">
        <Label htmlFor={id} className="font-semibold">{T.owner.label}</Label>
        <Input id={id} type="email" inputMode="email" autoComplete="off" className="w-64" placeholder={T.owner.placeholder} value={value} onChange={(e) => setValue(e.target.value)} />
        <Button size="sm" onClick={submit} disabled={save.isPending || value.trim().toLowerCase() === current}>{save.isPending ? T.saving : T.save}</Button>
        {current && <Button size="sm" variant="outline" onClick={() => remove.mutate()} disabled={remove.isPending}>{T.remove}</Button>}
        {setting.not_set ? <Badge variant="outline">{setting.not_set}</Badge> : <span className="text-xs text-ks-grey-1">{setBy(setting)}</span>}
      </div>
      <ResultLine result={result} />
    </Section>
  );
}

/** The stale-basis warn-after select (12, 24 or 48 hours). */
function StaleSetting({ setting, areaName }: { setting: Setting; areaName: string }) {
  const client = useQueryClient();
  const current = numberValue(setting) ?? "";
  const [value, setValue] = useState(current);
  const [result, setResult] = useState<Result>(null);
  useEffect(() => { setValue(current); }, [current]);
  const save = useMutation({
    mutationFn: (v: string) => putSetting("stale_basis_hours", { value: v }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: CONFIG_KEY }); setResult({ tone: "ok", text: T.saved }); },
    onError: (error) => setResult({ tone: "err", text: writeRefusal(error, areaName) }),
  });
  return (
    <Section title={T.stale.title} desc={T.stale.desc}>
      <div className="flex flex-wrap items-center gap-3">
        <Label className="font-semibold">{T.stale.label}</Label>
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger className="w-auto min-w-36" aria-label={T.stale.label}><SelectValue placeholder={setting.not_set ?? ""} /></SelectTrigger>
          <SelectContent>{STALE_HOURS.map((h) => <SelectItem key={h} value={h}>{T.stale.option(h)}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" onClick={() => value && save.mutate(value)} disabled={!value || value === current || save.isPending}>{save.isPending ? T.saving : T.save}</Button>
        {setting.not_set ? <Badge variant="outline">{setting.not_set}</Badge> : <span className="text-xs text-ks-grey-1">{setBy(setting)}</span>}
      </div>
      <ResultLine result={result} />
    </Section>
  );
}

/** RRP anchors: per region the anchor marketplace and every registry product with its RRP or "Not set". */
function RrpSection({ data, areaName }: { data: ConfigRead; areaName: string }) {
  return (
    <Section title={T.rrp.title} desc={T.rrp.desc}>
      {MARKETS.map((market) => <RrpRegionBlock key={market} market={market} region={data.rrp[market]} areaName={areaName} />)}
    </Section>
  );
}

function RrpRegionBlock({ market, region, areaName }: { market: Market; region: ConfigRead["rrp"][Market]; areaName: string }) {
  const client = useQueryClient();
  const anchor = anchorValue(region.anchor);
  const current = anchor?.marketplace_id ?? "";
  const [choice, setChoice] = useState(current);
  useEffect(() => { setChoice(current); }, [current]);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<Result>(null);
  const saveAnchor = useMutation({
    mutationFn: (id: string) => putSetting("rrp_anchor_market", { value: id, scope: market }),
    onSuccess: (s) => {
      void client.invalidateQueries({ queryKey: CONFIG_KEY });
      setResult({ tone: "ok", text: s.rrps_retired ? `${T.saved}. ${T.rrp.retired(s.rrps_retired)}` : T.saved });
    },
    onError: (error) => setResult({ tone: "err", text: writeRefusal(error, areaName) }),
  });
  const label = MARKET_LABEL[market];
  return (
    <div className="flex flex-col gap-2 border-t border-ks-line pt-3 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-semibold text-ks-ink">{label}</span>
        <Label className="text-sm">{T.rrp.anchorLabel}</Label>
        <Select value={choice} onValueChange={setChoice}>
          <SelectTrigger className="w-44" aria-label={`${T.rrp.anchorLabel} ${label}`}><SelectValue placeholder={T.rrp.pickAnchor} /></SelectTrigger>
          <SelectContent>{ANCHOR_OPTIONS[market].map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" onClick={() => choice && saveAnchor.mutate(choice)} disabled={!choice || choice === anchor?.marketplace_id || saveAnchor.isPending}>{saveAnchor.isPending ? T.saving : T.save}</Button>
        {anchor
          ? <span className="text-xs text-ks-grey-1">{anchor.marketplace_name ?? anchor.marketplace_id} · {setBy(region.anchor)}</span>
          : <Badge variant="outline">{region.anchor.not_set ?? T.rrp.noAnchor}</Badge>}
        <span className="text-sm text-ks-grey-1">{T.rrp.products(region.set_count, region.product_count)}</span>
        <Button size="sm" variant="outline" data-nonmutating aria-expanded={open} onClick={() => setOpen(!open)}>{open ? T.rrp.close : T.rrp.edit}</Button>
      </div>
      {region.set_count === 0 && <p className="text-xs text-ks-grey-1">{T.rrp.noneSet(label)}</p>}
      {/* Each RRP set for the region, readable without opening the editor (C1 v1.35, v26's inline list). */}
      {region.products.some((p) => p.rrp) && (
        <p className="text-xs text-ks-grey-1" data-rrp-list>{T.rrp.list(region.products.filter((p) => p.rrp).map((p) => `${p.tracking} ${p.variant} ${money(p.rrp, p.currency)}`).join(", "))}</p>
      )}
      <ResultLine result={result} />
      {open && (anchor
        ? <Table><TableHeader><TableRow>{T.rrp.productColumns.map((c, i) => <TableHead key={i}>{c}</TableHead>)}</TableRow></TableHeader>
          <TableBody>{region.products.map((p) => <RrpRow key={`${p.tracking}|${p.variant}`} market={market} product={p} areaName={areaName} />)}</TableBody></Table>
        : <p className="text-sm text-ks-warn">{T.rrp.anchorFirst(label)}</p>)}
    </div>
  );
}

function RrpRow({ market, product, areaName }: { market: Market; product: RrpProduct; areaName: string }) {
  const client = useQueryClient();
  const current = product.rrp ?? "";
  const [value, setValue] = useState(current);
  const [result, setResult] = useState<Result>(null);
  useEffect(() => { setValue(current); }, [current]);
  const refresh = () => void client.invalidateQueries({ queryKey: CONFIG_KEY });
  const save = useMutation({
    mutationFn: (rrp: string) => putRrp({ market, tracking: product.tracking, variant: product.variant, rrp }),
    onSuccess: () => { refresh(); setResult({ tone: "ok", text: T.saved }); },
    onError: (error) => setResult({ tone: "err", text: writeRefusal(error, areaName) }),
  });
  const remove = useMutation({
    mutationFn: () => deleteRrp(market, product.tracking, product.variant),
    onSuccess: () => { refresh(); setValue(""); setResult({ tone: "ok", text: T.removed }); },
    onError: (error) => setResult({ tone: "err", text: writeRefusal(error, areaName) }),
  });
  const submit = () => {
    const v = cleanNumber(value);
    if (!v) { setResult({ tone: "err", text: T.enterNumber }); return; }
    save.mutate(v);
  };
  return (
    <TableRow>
      <TableCell><span className="font-semibold text-ks-ink">{product.tracking} {product.variant}</span>{!product.active && <div className="text-xs text-ks-grey-1">{T.rrp.inactive}</div>}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Input inputMode="decimal" className="w-24" aria-label={`RRP ${product.tracking} ${product.variant}`} value={value} onChange={(e) => setValue(e.target.value)} />
          <span className="text-sm text-ks-grey-1">{product.rrp ? money(product.rrp, product.currency) : <Badge variant="outline">{T.rrp.notSet}</Badge>}</span>
        </div>
        <ResultLine result={result} />
      </TableCell>
      <TableCell className="text-xs text-ks-grey-1">{setBy(product)}{product.note && <div data-rrp-note>{product.note}</div>}</TableCell>
      <TableCell className="text-right">
        <Button size="sm" onClick={submit} disabled={save.isPending || value.trim() === current}>{T.save}</Button>
        {product.rrp && <Button size="sm" variant="outline" className="ml-2" onClick={() => remove.mutate()} disabled={remove.isPending}>{T.remove}</Button>}
      </TableCell>
    </TableRow>
  );
}

/** Own-channel floors: every active floor with Edit and Remove, and an Add row for a SKU without one. Remove retires on one
 * click with no confirm: a retire is reversible (set it again) and the history row stays, so a dialog would only add a step. */
function FloorsSection({ floors, areaName }: { floors: FloorRow[]; areaName: string }) {
  const client = useQueryClient();
  const [sku, setSku] = useState("");
  const [value, setValue] = useState("");
  const [result, setResult] = useState<Result>(null);
  const add = useMutation({
    mutationFn: (body: { sku: string; floor: string }) => putFloor(body.sku, { floor: body.floor }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: CONFIG_KEY }); setSku(""); setValue(""); setResult({ tone: "ok", text: T.saved }); },
    onError: (error) => setResult({ tone: "err", text: writeRefusal(error, areaName) }),
  });
  const submit = () => {
    const v = cleanNumber(value);
    const s = sku.trim();
    if (!s) { setResult({ tone: "err", text: T.floors.enterSku }); return; }
    if (!v) { setResult({ tone: "err", text: T.enterNumber }); return; }
    add.mutate({ sku: s, floor: v });
  };
  return (
    <Section title={T.floors.title} desc={T.floors.desc}>
      {floors.length === 0
        ? <p className="text-sm text-ks-grey-1">{T.floors.none}</p>
        : <Table><TableHeader><TableRow>{T.floors.columns.map((c, i) => <TableHead key={i}>{c}</TableHead>)}</TableRow></TableHeader>
          <TableBody>{floors.map((f) => <FloorRowView key={f.xero_sku} floor={f} areaName={areaName} />)}</TableBody></Table>}
      <div className="flex flex-wrap items-end gap-3 border-t border-ks-line pt-3">
        <div className="flex flex-col gap-1"><Label htmlFor="cfg-floor-sku" className="text-xs">{T.floors.skuLabel}</Label><Input id="cfg-floor-sku" className="w-40" value={sku} onChange={(e) => setSku(e.target.value)} /></div>
        <div className="flex flex-col gap-1"><Label htmlFor="cfg-floor-value" className="text-xs">{T.floors.floorLabel}</Label><Input id="cfg-floor-value" inputMode="decimal" className="w-24" value={value} onChange={(e) => setValue(e.target.value)} /></div>
        <Button size="sm" onClick={submit} disabled={add.isPending}>{T.floors.add}</Button>
      </div>
      <ResultLine result={result} />
    </Section>
  );
}

function FloorRowView({ floor, areaName }: { floor: FloorRow; areaName: string }) {
  const client = useQueryClient();
  const current = floor.floor ?? "";
  const [value, setValue] = useState(current);
  const [result, setResult] = useState<Result>(null);
  useEffect(() => { setValue(current); }, [current]);
  const refresh = () => void client.invalidateQueries({ queryKey: CONFIG_KEY });
  const save = useMutation({
    mutationFn: (v: string) => putFloor(floor.xero_sku, { floor: v }),
    onSuccess: () => { refresh(); setResult({ tone: "ok", text: T.saved }); },
    onError: (error) => setResult({ tone: "err", text: writeRefusal(error, areaName) }),
  });
  const remove = useMutation({
    mutationFn: () => deleteFloor(floor.xero_sku),
    onSuccess: () => { refresh(); setResult({ tone: "ok", text: T.removed }); },
    onError: (error) => setResult({ tone: "err", text: writeRefusal(error, areaName) }),
  });
  const submit = () => {
    const v = cleanNumber(value);
    if (!v) { setResult({ tone: "err", text: T.enterNumber }); return; }
    save.mutate(v);
  };
  return (
    <TableRow>
      <TableCell><span className="font-semibold text-ks-ink">{floor.product} {floor.variant}</span><div className="text-xs text-ks-grey-1">{floor.xero_sku} · {floor.channel} · {MARKET_LABEL[floor.market]}{floor.active ? "" : ` · ${T.rrp.inactive}`}</div></TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Input inputMode="decimal" className="w-24" aria-label={`Floor ${floor.xero_sku}`} value={value} onChange={(e) => setValue(e.target.value)} />
          <span className="text-sm tabular-nums text-ks-grey-1">{money(floor.floor, floor.currency)}</span>
        </div>
        <ResultLine result={result} />
      </TableCell>
      <TableCell><Badge variant={floor.source === "operator" ? "secondary" : "outline"}>{floorSourceLabel(floor.source)}</Badge><div className="text-xs text-ks-grey-1">{setBy(floor)}</div></TableCell>
      <TableCell className="text-right">
        <Button size="sm" onClick={submit} disabled={save.isPending || value.trim() === current}>{T.save}</Button>
        <Button size="sm" variant="outline" className="ml-2" onClick={() => remove.mutate()} disabled={remove.isPending}>{T.remove}</Button>
      </TableCell>
    </TableRow>
  );
}

/** Serving sizes (ruling S-F): every registry product with its three numbers or "Not set", closed until Edit, as an RRP region. */
function ServingSection({ sizes, areaName }: { sizes: ServingSizes; areaName: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Section title={T.serving.title} desc={T.serving.desc}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-ks-grey-1">{T.serving.products(sizes.set_count, sizes.product_count)}</span>
        <Button size="sm" variant="outline" data-nonmutating aria-expanded={open} onClick={() => setOpen(!open)}>{open ? T.rrp.close : T.rrp.edit}</Button>
      </div>
      {open && <Table><TableHeader><TableRow>{T.serving.columns.map((c, i) => <TableHead key={i}>{c}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{sizes.products.map((p) => <ServingRow key={`${p.tracking}|${p.variant}`} product={p} areaName={areaName} />)}</TableBody></Table>}
    </Section>
  );
}

function ServingRow({ product, areaName }: { product: ServingProduct; areaName: string }) {
  const client = useQueryClient();
  const current = { servings: product.servings_per_pack ?? "", max: product.max_daily_servings ?? "", units: product.units_per_pack == null ? "" : String(product.units_per_pack) };
  const [servings, setServings] = useState(current.servings);
  const [max, setMax] = useState(current.max);
  const [units, setUnits] = useState(current.units);
  const [result, setResult] = useState<Result>(null);
  useEffect(() => { setServings(current.servings); setMax(current.max); setUnits(current.units); }, [current.servings, current.max, current.units]);
  // A serving size moves the Customer lens, the strategy chips and a projection at a typed price: drop them with Config.
  const refresh = () => { for (const queryKey of [CONFIG_KEY, CUSTOMER_KEY, EVAL_KEY, PROJECTION_KEY]) void client.invalidateQueries({ queryKey }); };
  const save = useMutation({
    mutationFn: (body: { servings_per_pack: string; max_daily_servings: string | null; units_per_pack: string | null }) =>
      putServing({ tracking: product.tracking, variant: product.variant, ...body }),
    onSuccess: () => { refresh(); setResult({ tone: "ok", text: T.saved }); },
    onError: (error) => setResult({ tone: "err", text: writeRefusal(error, areaName) }),
  });
  const remove = useMutation({
    mutationFn: () => deleteServing(product.tracking, product.variant),
    onSuccess: () => { refresh(); setResult({ tone: "ok", text: T.removed }); },
    onError: (error) => setResult({ tone: "err", text: writeRefusal(error, areaName) }),
  });
  const submit = () => {
    const s = cleanPositive(servings);
    if (!s) { setResult({ tone: "err", text: T.serving.enterServings }); return; }
    const m = max.trim() ? cleanPositive(max) : null;
    if (max.trim() && !m) { setResult({ tone: "err", text: T.enterNumber }); return; }
    const u = units.trim() ? cleanWhole(units) : null;
    if (units.trim() && !u) { setResult({ tone: "err", text: T.serving.wholeUnits }); return; }
    save.mutate({ servings_per_pack: s, max_daily_servings: m, units_per_pack: u });
  };
  const unchanged = servings.trim() === current.servings && max.trim() === current.max && units.trim() === current.units;
  const name = `${product.tracking} ${product.variant}`;
  return (
    <TableRow>
      <TableCell><span className="font-semibold text-ks-ink">{name}</span>{product.active === false && <div className="text-xs text-ks-grey-1">{T.rrp.inactive}</div>}
        {product.servings_per_pack == null ? <div><Badge variant="outline">{T.rrp.notSet}</Badge></div> : <div className="text-xs text-ks-grey-1">{setBy(product)}</div>}</TableCell>
      <TableCell><Input inputMode="decimal" className="w-20" aria-label={`${T.serving.servingsLabel} ${name}`} value={servings} onChange={(e) => setServings(e.target.value)} /></TableCell>
      <TableCell><Input inputMode="decimal" className="w-20" aria-label={`${T.serving.maxLabel} ${name}`} placeholder={T.serving.optional} value={max} onChange={(e) => setMax(e.target.value)} /></TableCell>
      <TableCell><Input inputMode="numeric" className="w-20" aria-label={`${T.serving.unitsLabel} ${name}`} placeholder={T.serving.optional} value={units} onChange={(e) => setUnits(e.target.value)} />
        <ResultLine result={result} /></TableCell>
      <TableCell className="text-right">
        <Button size="sm" onClick={submit} disabled={save.isPending || unchanged}>{T.save}</Button>
        {product.servings_per_pack != null && <Button size="sm" variant="outline" className="ml-2" onClick={() => remove.mutate()} disabled={remove.isPending}>{T.remove}</Button>}
      </TableCell>
    </TableRow>
  );
}

/** Currency links, read-only (each currency has one rate source). The FX source cell follows the data: each market's last daily
 * grid (the Price Run's own cache entry) says whether it converted any of that channel's costs. C1 v1.49: C2 v1.51's unmapped
 * currencies read as a banner above the table and one row each after the links. */
function LinksSection({ data }: { data: ConfigRead }) {
  const grids = useQueries({ queries: MARKETS.map((market) => gridQuery(market)) });
  const gridOf = (market: Market) => grids[MARKETS.indexOf(market)]?.data?.grid;
  return (
    <Section title={T.links.title} desc={T.links.desc}>
      {unmappedTotal(data) > 0 && <Alert role="note" data-unmapped=""><AlertDescription>{T.links.unmappedBanner(unmappedTotal(data))}</AlertDescription></Alert>}
      <Table><TableHeader><TableRow>{T.links.columns.map((c, i) => <TableHead key={i}>{c}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{data.currency_links.map((l) => (
          <TableRow key={`${l.market}|${l.channel}`}>
            <TableCell><span className="font-semibold text-ks-ink">{MARKET_LABEL[l.market]} · {l.channel}</span><div className="text-xs text-ks-grey-1">{ENTITY_LABEL[l.entity_id] ?? l.entity_id}</div></TableCell>
            <TableCell>{l.native_currency}</TableCell>
            <TableCell>{fxSourceLabel(l, gridOf(l.market))}</TableCell>
          </TableRow>))}
          {unmappedOf(data).map((u) => (
            <TableRow key={`unmapped|${u.currency}`} data-unmapped-row={u.currency}>
              <TableCell><span className="font-semibold text-ks-ink">{unmappedInputs(u)}</span></TableCell>
              <TableCell>{u.currency} <Badge variant="secondary">{T.links.unmapped}</Badge></TableCell>
              <TableCell>{T.links.notSet}<div className="text-xs text-ks-grey-1">{T.links.report}</div></TableCell>
            </TableRow>))}</TableBody></Table>
    </Section>
  );
}
